import { effect, inject, Injectable } from '@angular/core';

import { FilterApiService } from '../api/filter.api.service';
import { Filter } from '../../domain/interfaces/Filter.interface';
import { FilterStore } from './filter.store';
import { DATE_FILTER_INITIAL } from '../../domain/constants/filter.constant';
import { FilterValidateService } from './filter-validate.service';
import { Title } from '@angular/platform-browser';
import { UserService } from './user.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class FilterService {

  private readonly filterApiService: FilterApiService = inject(FilterApiService)
  private readonly userService = inject(UserService);
  private readonly authService = inject(AuthService);
  private readonly store = inject(FilterStore)
  private readonly validate = inject(FilterValidateService)
  private readonly title = inject(Title)

  // Public state
  readonly filter = this.store.filter

  //  Filter options
  readonly states = this.store.states
  readonly municipalities = this.store.municipalities
  readonly indigenousLands = this.store.indigenousLands
  readonly conservationUnits = this.store.conservationUnits
  readonly status = this.store.status
  readonly maintenance = this.store.maintenance
  readonly contractType = this.store.contractType
  readonly connectionType = this.store.connectionType
  readonly partners = this.store.partners
  readonly specialTerritories = this.store.specialTerritories
  readonly energyKitModel = this.store.energyKitModel
  readonly declaredTerritory = this.store.declaredTerritory
  readonly workGroups = this.store.workGroups
  readonly association = this.store.association
  readonly routerModel = this.store.routerModel

  // Loading state
  private _isFilterChanging = false;
  private _lastProcessedFilter: string = '';

  filterOptions: Filter = {
    states: [],
    municipalities: [],
    indigenousLands: [],
    conservationUnits: [],
    status: [],
    maintenance: null,
    contractType: null,
    connectionType: [],
    partners: [],
    specialTerritories: [],
    energyKitModel: [],
    dateStart: DATE_FILTER_INITIAL.dateStart,
    dateEnd: DATE_FILTER_INITIAL.dateEnd,
    declaredTerritory: [],
    workGroups: [],
    association: null,
    routerModel: []
  }

  constructor() {
    effect(() => {
      const currentFilter = this.store.filter();
      if (
        this.title.getTitle() !== 'Painel de Consulta' &&
        this.title.getTitle() !== 'Povos da Floresta - Portal de Gestao'
      ) {
        return
      }

      const filterJson = JSON.stringify(currentFilter);

      if (this._isFilterChanging || filterJson === this._lastProcessedFilter) {
        return;
      }


      if (Object.keys(currentFilter).length > 0) {
        

        this._isFilterChanging = true;
        this._lastProcessedFilter = filterJson;

        this.loadStatesOptions(currentFilter);
        this.loadMunicipalitiesOptions(currentFilter);
        this.loadIndigenousLandsOptions(currentFilter);
        this.loadConservationUnitsOptions(currentFilter);
        this.loadSpecialTerritoriesOptions(currentFilter);
        this.loadStatusOptions(currentFilter);
        this.loadMaintenanceOptions(currentFilter);
        this.loadContractTypeOptions(currentFilter);
        this.loadConnectionTypeOptions(currentFilter);
        this.loadPartnersOptions(currentFilter);
        this.loadEnergyKitModelOptions(currentFilter);
        this.loadDeclaredTerritoryOptions(currentFilter);
        this.loadWorkGroupsOptions(currentFilter);
        this.loadAssociationOptions(currentFilter);
        this.loadRouterModelOptions(currentFilter);

        setTimeout(() => {
          this._isFilterChanging = false;
        }, 100);
      }
    })
  }

  // Public methods
  applyFilter(filter: Filter): void {

    // Update store with validates filter
    this.store.setFilter(filter);
  }

  clearFilter() {
    this.store.resetFilter();
  }

  public async loadStatesOptions(filter: Filter): Promise<void> {
    const { states, ...rest } = filter;
    try {
      const options = await this.filterApiService.getStateOptions(rest);
      this.store.updateStates(options);

      const currentFilter = this.store.filter();
      if (currentFilter.states?.length) {
        const validClasses = this.validate.validateStates(
          currentFilter.states,
          options
        );
        if (validClasses.length !== currentFilter.states.length) {
          this.store.updateFilter({ states: validClasses });
        }
      }
    } catch (error) {
      throw new Error('Erro ao obter estados');
    }
  }

  public async loadMunicipalitiesOptions(filter: Filter): Promise<void> {
    const { municipalities, ...rest } = filter;
    try {
      const options = await this.filterApiService.getMunicipalitiesOptionsV2(rest);
      this.store.updateMunicipalities(options);

      const currentFilter = this.store.filter();
      if (currentFilter.municipalities?.length) {
        const validClasses = this.validate.validateMunicipalities(
          currentFilter.municipalities,
          options
        );
        if (validClasses.length !== currentFilter.municipalities.length) {
          this.store.updateFilter({ municipalities: validClasses });
        }
      }
    } catch (error) {
      throw new Error('Erro ao obter municípios');
    }
  }

  public async loadIndigenousLandsOptions(filter: Filter): Promise<void> {
    const { indigenousLands, ...rest } = filter;
    try {
      const options = await this.filterApiService.getIndigenousLandsOptionsV2(rest);
      this.store.updateIndigenousLands(options);

      const currentFilter = this.store.filter();
      if (currentFilter.indigenousLands?.length) {
        const validClasses = this.validate.validateIndigenousLands(
          currentFilter.indigenousLands,
          options
        );
        if (validClasses.length !== currentFilter.indigenousLands.length) {
          this.store.updateFilter({ indigenousLands: validClasses });
        }
      }
    } catch (error) {
      throw new Error('Erro ao obter terras indígenas');
    }
  }

  public async loadConservationUnitsOptions(filter: Filter): Promise<void> {
    const { conservationUnits, ...rest } = filter;
    try {
      const options = await this.filterApiService.getConservationUnitsOptionsV2(rest);
      this.store.updateConservationUnits(options);

      const currentFilter = this.store.filter();
      if (currentFilter.conservationUnits?.length) {
        const validClasses = this.validate.validateConservationUnits(
          currentFilter.conservationUnits,
          options
        );
        if (validClasses.length !== currentFilter.conservationUnits.length) {
          this.store.updateFilter({ conservationUnits: validClasses });
        }
      }
    } catch (error) {
      throw new Error('Erro ao obter unidades de conservação');
    }
  }

  public async loadSpecialTerritoriesOptions(filter: Filter): Promise<void> {
    const { specialTerritories, ...rest } = filter;
    try {
      const options = await this.filterApiService.getSpecialTerritoriesOptionsV2(rest);
      this.store.updateSpecialTerritories(options);

      const currentFilter = this.store.filter();
      if (currentFilter.specialTerritories?.length) {
        const validClasses = this.validate.validateSpecialTerritories(
          currentFilter.specialTerritories,
          options
        );
        if (validClasses.length !== currentFilter.specialTerritories.length) {
          this.store.updateFilter({ specialTerritories: validClasses });
        }
      }
    } catch (error) {
      throw new Error('Erro ao obter terras especiais');
    }
  }

  public async loadStatusOptions(filter: Filter): Promise<void> {
    const { status, ...rest } = filter;
    try {
      const options = await this.filterApiService.getStatusOptions(rest);
      this.store.updateStatus(options);

      const currentFilter = this.store.filter();
      if (currentFilter.status?.length) {
        const validClasses = this.validate.validateStatus(
          currentFilter.status,
          options
        );
        if (validClasses.length !== currentFilter.status.length) {
          this.store.updateFilter({ status: validClasses });
        }
      }
    } catch (error) {
      throw new Error('Erro ao obter status');
    }
  }

  public async loadMaintenanceOptions(filter: Filter): Promise<void> {
    // const { maintenance, ...rest } = filter;
    try {
      const options = await this.filterApiService.getMaintenanceStatusOptions(filter);
      this.store.updateMaintenance(options);

    } catch (error) {
      throw new Error('Erro ao obter manutenção');
    }
  }

  public async loadContractTypeOptions(filter: Filter): Promise<void> {
    // const { contractType, ...rest } = filter;
    try {
      const options = await this.filterApiService.getContractTypeOptions(filter);
      this.store.updateContractType(options);
    } catch (error) {
      throw new Error('Erro ao obter tipo de contrato');
    }
  }

  public async loadConnectionTypeOptions(filter: Filter): Promise<void> {
    const { connectionType, ...rest } = filter;
    try {
      const options = await this.filterApiService.getConnectionTypeOptions(rest);
      this.store.updateConnectionType(options);

      const currentFilter = this.store.filter();
      if (currentFilter.connectionType?.length) {
        const validClasses = this.validate.validateConnectionType(
          currentFilter.connectionType,
          options
        );
        if (validClasses.length !== currentFilter.connectionType.length) {
          this.store.updateFilter({ connectionType: validClasses });
        }
      }
    } catch (error) {
      throw new Error('Erro ao obter tipo de conexão');
    }
  }

  public async loadPartnersOptions(filter: Filter): Promise<void> {
    const { partners, ...rest } = filter;
    try {
      const options = await this.filterApiService.getPartnerOptions(rest);
      const user = this.userService.getUser();

      // Adding logged user's partner, in case the partner hasn't any installed KITs
      if(user?.partnerId && !options.some(partner => partner.id === user?.partnerId)) {
        options.push({ id: user?.partnerId, name: user?.partnerName });
      }

      this.store.updatePartners(options);

      const currentFilter = this.store.filter();
      if (currentFilter.partners?.length) {
        const validClasses = this.validate.validatePartner(
          currentFilter.partners,
          options
        );
        if (validClasses.length !== currentFilter.partners.length) {
          this.store.updateFilter({ partners: validClasses });
        }
      }
    } catch (error) {
      throw new Error('Erro ao obter parceiros');
    }
  }

  public async loadEnergyKitModelOptions(filter: Filter): Promise<void> {
    const { energyKitModel, ...rest } = filter;
    try {
      const options = await this.filterApiService.getEnergyKitModelOptions(rest);
      this.store.updateEnergyKitModel(options);

      const currentFilter = this.store.filter();
      if (currentFilter.energyKitModel?.length) {
        const validClasses = this.validate.validateEnergyKitModel(
          currentFilter.energyKitModel,
          options
        );
        if (validClasses.length !== currentFilter.energyKitModel.length) {
          this.store.updateFilter({ energyKitModel: validClasses });
        }
      }
    } catch (error) {
      throw new Error('Erro ao obter modelos de kit de energia');
    }
  }

  public async loadDeclaredTerritoryOptions(filter: Filter): Promise<void> {
    const { declaredTerritory, ...rest } = filter;
    try {
      const options = await this.filterApiService.getDeclaredTerritoryOptions(rest);
      this.store.updateDeclaredTerritory(options);

      const currentFilter = this.store.filter();
      if (currentFilter.declaredTerritory?.length) {
        const validClasses = this.validate.validateDeclaredTerritory(
          currentFilter.declaredTerritory,
          options
        );
        if (validClasses.length !== currentFilter.declaredTerritory.length) {
          this.store.updateFilter({ declaredTerritory: validClasses });
        }
      }
    } catch (error) {
      throw new Error('Erro ao obter terras declaradas');
    }
  }
  public async loadWorkGroupsOptions(filter: Filter): Promise<void> {
    const { workGroups, ...rest } = filter;
    try {
      const options = await this.filterApiService.getWorkGroupsOptions(rest);
      this.store.updateWorkGroups(options);

      const currentFilter = this.store.filter();
      if (currentFilter.workGroups?.length) {
        const validClasses = this.validate.validateWorkGroups(
          currentFilter.workGroups,
          options
        );
        if (validClasses.length !== currentFilter.workGroups.length) {
          this.store.updateFilter({ workGroups: validClasses });
        }
      }
    } catch (error) {
      throw new Error('Erro ao obter grupos de trabalho');
    }
  }

  public async loadAssociationOptions(filter: Filter): Promise<void> {
    try {
      const options = await this.filterApiService.getAssociationOptions(filter);
      this.store.updateAssociation(options);

    } catch (error) {
      throw new Error('Erro ao obter opções de documento de associação');
    }
  }

  public async loadRouterModelOptions(filter: Filter): Promise<void> {
    const { routerModel, ...rest } = filter;
    try {
      const options = await this.filterApiService.getRouterModelOptions(rest);
      this.store.updateRouterModel(options);

      const currentFilter = this.store.filter();
      if (currentFilter.routerModel?.length) {
        const validClasses = this.validate.validateRouterModel(
          currentFilter.routerModel,
          options
        );
        if (validClasses.length !== currentFilter.routerModel.length) {
          this.store.updateFilter({ routerModel: validClasses });
        }
      }
    } catch (error) {
      throw new Error('Erro ao obter modelos de roteador');
    }
  }

  // Event Handling
  getCurrentFilter(): Filter {
    return this.store.filter();
  }

  capitalizeFirstLetter(str: string): string {
    return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  }
}
